import type { MetaState, UserSettings } from "../store";
import type { LanguageId } from "./references";

type StoredUserSettings = Partial<UserSettings> & {
  /** Accepted only so v0-v3 profiles can discard the retired dual-flow setting. */
  defaultStudyMode?: unknown;
};

export type StoredMetaState = {
  schemaVersion?: number;
  profile?: Partial<MetaState["profile"]>;
  settings?: StoredUserSettings;
  activity?: MetaState["activity"];
};

const CURRENT_META_SCHEMA = 4;

function uniqueLearning(primary: LanguageId, value: unknown): LanguageId[] {
  if (!Array.isArray(value)) return [];
  const result: LanguageId[] = [];
  for (const candidate of value) {
    if (typeof candidate !== "string" || candidate === primary) continue;
    const language = candidate as LanguageId;
    if (!result.includes(language)) result.push(language);
  }
  return result;
}

function activeLanguages(primary: LanguageId, learning: LanguageId[]): LanguageId[] {
  return [primary, ...learning.filter((language) => language !== primary)];
}

function migrateV0ToV1(state: StoredMetaState): StoredMetaState {
  const settings = { ...(state.settings ?? {}) };
  const legacyActive = Array.isArray(settings.activeLangs)
    ? [...settings.activeLangs]
    : [];
  const primary = settings.primaryLang ?? legacyActive[0];
  if (primary) {
    settings.primaryLang = primary;
    if (!Array.isArray(settings.learningLangs)) {
      settings.learningLangs = legacyActive.filter((language) => language !== primary);
    }
  }
  return { ...state, schemaVersion: 1, settings };
}

function migrateV1ToV2(state: StoredMetaState): StoredMetaState {
  const settings = { ...(state.settings ?? {}) };
  if (settings.defaultStudyMode === "drill") settings.defaultStudyMode = "flashcard";
  return { ...state, schemaVersion: 2, settings };
}

function migrateV2ToV3(state: StoredMetaState): StoredMetaState {
  const settings = { ...(state.settings ?? {}) };
  if (settings.primaryLang) {
    const learning = uniqueLearning(settings.primaryLang, settings.learningLangs);
    settings.learningLangs = learning;
    settings.activeLangs = activeLanguages(settings.primaryLang, learning);
  }
  return { ...state, schemaVersion: 3, settings };
}

function migrateV3ToV4(state: StoredMetaState): StoredMetaState {
  const settings = { ...(state.settings ?? {}) };
  delete settings.defaultStudyMode;
  return { ...state, schemaVersion: 4, settings };
}

/** Pure, stepwise metadata migration. Dexie learning rows are deliberately
 * outside this function: directions and schedules remain byte-for-byte as
 * stored, while only their language display settings are normalized. */
export function migrateStoredMetaState(
  stored: unknown,
  defaults: MetaState,
): MetaState {
  const parsed: StoredMetaState =
    stored && typeof stored === "object" ? { ...(stored as StoredMetaState) } : {};
  let working: StoredMetaState = {
    ...parsed,
    profile: parsed.profile ? { ...parsed.profile } : undefined,
    settings: parsed.settings ? { ...parsed.settings } : undefined,
    activity: parsed.activity ? [...parsed.activity] : undefined,
  };
  let version = Number.isInteger(working.schemaVersion)
    ? Math.max(0, working.schemaVersion ?? 0)
    : 0;
  while (version < CURRENT_META_SCHEMA) {
    if (version === 0) working = migrateV0ToV1(working);
    else if (version === 1) working = migrateV1ToV2(working);
    else if (version === 2) working = migrateV2ToV3(working);
    else working = migrateV3ToV4(working);
    version = working.schemaVersion ?? version + 1;
  }

  const storedSettings = { ...(working.settings ?? {}) };
  delete storedSettings.defaultStudyMode;
  const settings = { ...defaults.settings, ...storedSettings };
  const learning = uniqueLearning(settings.primaryLang, settings.learningLangs);
  settings.learningLangs = learning;
  settings.activeLangs = activeLanguages(settings.primaryLang, learning);
  return {
    schemaVersion: CURRENT_META_SCHEMA,
    profile: { ...defaults.profile, ...(working.profile ?? {}) },
    settings,
    activity: working.activity ? [...working.activity] : [],
  };
}
